import React, { useState } from 'react';
import { BookOpen, Brain, Network, Database, CheckCircle, AlertCircle, ChevronRight, ChevronLeft } from 'lucide-react';

const KnowledgeRepresentationTutor = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showQuizResults, setShowQuizResults] = useState(false);

  const QuizSection = ({ quizAnswers, setQuizAnswers }) => {
    const questions = [
      {
        id: 1,
        question: "What is the main limitation of Propositional Logic?",
        options: [
          "It cannot handle true/false values",
          "It cannot express statements about groups or relationships",
          "It is too complex to use",
          "It cannot use logical operators"
        ],
        correct: 1
      },
      {
        id: 2,
        question: "Which logical operator should be used with Universal Quantifier (∀)?",
        options: [
          "∧ (AND)",
          "∨ (OR)",
          "→ (IMPLIES)",
          "↔ (IFF)"
        ],
        correct: 2
      },
      {
        id: 3,
        question: "Which logical operator should be used with Existential Quantifier (∃)?",
        options: [
          "∧ (AND)",
          "∨ (OR)",
          "→ (IMPLIES)",
          "↔ (IFF)"
        ],
        correct: 0
      },
      {
        id: 4,
        question: "In a semantic network, what does an 'is-a' link represent?",
        options: [
          "A specific instance of a class",
          "A class hierarchy relationship",
          "Ownership relationship",
          "A property of an object"
        ],
        correct: 1
      },
      {
        id: 5,
        question: "What does 'Inferential Adequacy' mean in KR evaluation?",
        options: [
          "Ability to represent all kinds of knowledge",
          "Ability to derive new knowledge from old knowledge",
          "Ability to acquire knowledge easily",
          "Ability to store knowledge efficiently"
        ],
        correct: 1
      },
      {
        id: 6,
        question: "How would you write 'All students are smart' in predicate logic?",
        options: [
          "∃x (Student(x) ∧ Smart(x))",
          "∀x (Student(x) ∧ Smart(x))",
          "∀x (Student(x) → Smart(x))",
          "∃x (Student(x) → Smart(x))"
        ],
        correct: 2
      },
      {
        id: 7,
        question: "How would you write 'Some students are tired' in predicate logic?",
        options: [
          "∀x (Student(x) → Tired(x))",
          "∃x (Student(x) → Tired(x))",
          "∀x (Student(x) ∧ Tired(x))",
          "∃x (Student(x) ∧ Tired(x))"
        ],
        correct: 3
      },
      {
        id: 8,
        question: "In the truth table, when is P → Q false?",
        options: [
          "When P is true and Q is false",
          "When P is false and Q is true",
          "When both P and Q are true",
          "When both P and Q are false"
        ],
        correct: 0
      },
      {
        id: 9,
        question: "What type of knowledge describes 'how to do something'?",
        options: [
          "Declarative Knowledge",
          "Procedural Knowledge",
          "Meta Knowledge",
          "Heuristic Knowledge"
        ],
        correct: 1
      },
      {
        id: 10,
        question: "In semantic networks, what does an 'instance' link represent?",
        options: [
          "A class hierarchy",
          "A specific example of a class",
          "A property relationship",
          "A part-whole relationship"
        ],
        correct: 1
      }
    ];

    return (
      <div className="space-y-4">
        {questions.map((q, idx) => (
          <div key={q.id} className="bg-white p-5 rounded-lg shadow-md border-l-4 border-blue-500">
            <p className="font-bold text-gray-800 mb-3">
              {idx + 1}. {q.question}
            </p>
            <div className="space-y-2">
              {q.options.map((option, optIdx) => (
                <label key={optIdx} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="radio"
                    name={`question-${q.id}`}
                    value={optIdx}
                    checked={quizAnswers[q.id] === optIdx}
                    onChange={() => setQuizAnswers({...quizAnswers, [q.id]: optIdx})}
                    className="w-4 h-4"
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const QuizResults = ({ quizAnswers }) => {
    const questions = [
      { id: 1, correct: 1, explanation: "Propositional logic cannot express statements about groups (like 'all students') or relationships. That's why we need Predicate Logic." },
      { id: 2, correct: 2, explanation: "Universal quantifier (∀) uses → (IMPLIES). Example: ∀x (Student(x) → Smart(x)) means 'IF something is a student, THEN it is smart.'" },
      { id: 3, correct: 0, explanation: "Existential quantifier (∃) uses ∧ (AND). Example: ∃x (Student(x) ∧ Smart(x)) means 'There exists something that IS a student AND smart.'" },
      { id: 4, correct: 1, explanation: "'is-a' represents class hierarchy. Example: Dog is-a Mammal means Dog is a type/subclass of Mammal." },
      { id: 5, correct: 1, explanation: "Inferential Adequacy is the ability to manipulate knowledge structures to derive new knowledge from existing knowledge through reasoning." },
      { id: 6, correct: 2, explanation: "∀x (Student(x) → Smart(x)) - Universal quantifier with implication. 'For all x, IF x is a student, THEN x is smart.'" },
      { id: 7, correct: 3, explanation: "∃x (Student(x) ∧ Tired(x)) - Existential quantifier with conjunction. 'There exists an x that IS a student AND tired.'" },
      { id: 8, correct: 0, explanation: "P → Q is only false when P is true and Q is false. In all other cases, it's true. This is the most important row in the truth table!" },
      { id: 9, correct: 1, explanation: "Procedural Knowledge describes 'HOW' to do something (steps/procedures). Declarative Knowledge describes 'WHAT' (facts)." },
      { id: 10, correct: 1, explanation: "'instance' represents a specific example of a class. Example: Fido instance Dog means Fido is a specific dog." }
    ];

    let score = 0;
    questions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) score++;
    });

    const percentage = (score / questions.length * 100).toFixed(0);
    const passed = percentage >= 70;

    return (
      <div className="space-y-4">
        <div className={`p-6 rounded-lg ${passed ? 'bg-green-50 border-l-4 border-green-500' : 'bg-yellow-50 border-l-4 border-yellow-500'}`}>
          <h4 className="text-2xl font-bold mb-2">
            {passed ? '🎉 Excellent!' : '📚 Keep Practicing!'}
          </h4>
          <p className="text-xl">
            Your Score: <span className="font-bold">{score}/{questions.length}</span> ({percentage}%)
          </p>
          {passed ? (
            <p className="text-green-700 mt-2">You're ready for the exam! Review any mistakes below.</p>
          ) : (
            <p className="text-yellow-700 mt-2">Review the material and try again. Focus on the explanations below.</p>
          )}
        </div>

        {questions.map((q, idx) => {
          const userAnswer = quizAnswers[q.id];
          const isCorrect = userAnswer === q.correct;
          
          return (
            <div key={q.id} className={`p-4 rounded-lg ${isCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-start gap-2">
                {isCorrect ? (
                  <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={20} />
                ) : (
                  <AlertCircle className="text-red-600 mt-1 flex-shrink-0" size={20} />
                )}
                <div>
                  <p className="font-bold text-gray-800">Question {idx + 1}</p>
                  {!isCorrect && userAnswer !== undefined && (
                    <p className="text-red-700 text-sm mt-1">Your answer was incorrect</p>
                  )}
                  <p className="text-gray-700 text-sm mt-2">
                    <span className="font-semibold">Explanation:</span> {q.explanation}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const sections = [
    {
      id: 0,
      title: "1. Foundation: What is Knowledge Representation?",
      icon: Brain,
      color: "bg-blue-500",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-l-4 border-blue-500">
            <h3 className="text-2xl font-bold text-blue-900 mb-4">🎯 The Big Picture</h3>
            <p className="text-lg text-gray-700 mb-4">
              Imagine you're teaching a robot about the world. How do you store information in its "brain"? 
              That's Knowledge Representation (KR)!
            </p>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="font-semibold text-gray-800">Knowledge = Information + Structure</p>
              <p className="text-gray-600 mt-2">It's not just storing facts, but organizing them so the AI can reason and make decisions.</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white p-5 rounded-lg shadow-md border-t-4 border-green-500">
              <h4 className="font-bold text-lg text-green-700 mb-3">📊 The AI Cycle</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-bold">1</span>
                  <div>
                    <p className="font-semibold">Perception</p>
                    <p className="text-gray-600">Get info from environment (see, hear, sense)</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-bold">2</span>
                  <div>
                    <p className="font-semibold">Knowledge Representation</p>
                    <p className="text-gray-600">Store info in a structured way</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-bold">3</span>
                  <div>
                    <p className="font-semibold">Reasoning</p>
                    <p className="text-gray-600">Use the knowledge to make decisions</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-bold">4</span>
                  <div>
                    <p className="font-semibold">Action</p>
                    <p className="text-gray-600">Take action based on reasoning</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-5 rounded-lg shadow-md border-t-4 border-purple-500">
              <h4 className="font-bold text-lg text-purple-700 mb-3">⚖️ Evaluating KR Systems</h4>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-semibold text-purple-900">Representational Adequacy</p>
                  <p className="text-gray-600">Can it represent ALL needed knowledge?</p>
                </div>
                <div>
                  <p className="font-semibold text-purple-900">Inferential Adequacy</p>
                  <p className="text-gray-600">Can it derive NEW knowledge from OLD?</p>
                </div>
                <div>
                  <p className="font-semibold text-purple-900">Inferential Efficiency</p>
                  <p className="text-gray-600">How FAST can it reason?</p>
                </div>
                <div>
                  <p className="font-semibold text-purple-900">Acquisition Efficiency</p>
                  <p className="text-gray-600">How EASY to add new knowledge?</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-5 rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-bold text-lg text-yellow-800 mb-3">📚 Types of Knowledge</h4>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="bg-white p-3 rounded shadow-sm">
                <p className="font-semibold text-gray-800">Declarative: "WHAT"</p>
                <p className="text-gray-600 italic">Facts: "Paris is capital of France"</p>
              </div>
              <div className="bg-white p-3 rounded shadow-sm">
                <p className="font-semibold text-gray-800">Procedural: "HOW"</p>
                <p className="text-gray-600 italic">Steps: "How to make tea"</p>
              </div>
              <div className="bg-white p-3 rounded shadow-sm">
                <p className="font-semibold text-gray-800">Meta Knowledge</p>
                <p className="text-gray-600 italic">Knowledge about knowledge</p>
              </div>
              <div className="bg-white p-3 rounded shadow-sm">
                <p className="font-semibold text-gray-800">Heuristic</p>
                <p className="text-gray-600 italic">Rules of thumb, shortcuts</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 1,
      title: "2. Propositional Logic - The Foundation",
      icon: Database,
      color: "bg-green-500",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border-l-4 border-green-500">
            <h3 className="text-2xl font-bold text-green-900 mb-4">🔤 Propositional Logic Basics</h3>
            <p className="text-lg text-gray-700 mb-4">
              Think of propositions as simple statements that are either <span className="font-bold text-green-700">TRUE</span> or <span className="font-bold text-red-700">FALSE</span>. 
              No in-between!
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white p-5 rounded-lg shadow-md">
              <h4 className="font-bold text-lg mb-3 text-green-700">✅ Valid Propositions</h4>
              <div className="space-y-2 text-sm">
                <p className="bg-green-50 p-2 rounded">P: "It is raining" → TRUE or FALSE</p>
                <p className="bg-green-50 p-2 rounded">Q: "I am hungry" → TRUE or FALSE</p>
                <p className="bg-green-50 p-2 rounded">R: "2 + 2 = 4" → TRUE</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-lg shadow-md">
              <h4 className="font-bold text-lg mb-3 text-red-700">❌ NOT Propositions</h4>
              <div className="space-y-2 text-sm">
                <p className="bg-red-50 p-2 rounded">"What time is it?" → Question</p>
                <p className="bg-red-50 p-2 rounded">"Go to sleep!" → Command</p>
                <p className="bg-red-50 p-2 rounded">"x + 5 = 10" → Has variable (needs value)</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="font-bold text-xl mb-4 text-blue-900">🔧 Logical Operators (Connectives)</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="p-3 text-left">Operator</th>
                    <th className="p-3 text-left">Symbol</th>
                    <th className="p-3 text-left">Example</th>
                    <th className="p-3 text-left">Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="p-3 font-semibold">NOT</td>
                    <td className="p-3">~P or ¬P</td>
                    <td className="p-3">~P</td>
                    <td className="p-3">Opposite of P</td>
                  </tr>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="p-3 font-semibold">AND</td>
                    <td className="p-3">P ∧ Q</td>
                    <td className="p-3">P ∧ Q</td>
                    <td className="p-3">Both must be true</td>
                  </tr>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="p-3 font-semibold">OR</td>
                    <td className="p-3">P ∨ Q</td>
                    <td className="p-3">P ∨ Q</td>
                    <td className="p-3">At least one is true</td>
                  </tr>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="p-3 font-semibold">IMPLIES</td>
                    <td className="p-3">P → Q</td>
                    <td className="p-3">P → Q</td>
                    <td className="p-3">If P then Q</td>
                  </tr>
                  <tr className="border-b hover:bg-gray-50">
                    <td className="p-3 font-semibold">IFF</td>
                    <td className="p-3">P ↔ Q</td>
                    <td className="p-3">P ↔ Q</td>
                    <td className="p-3">P and Q are equivalent</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="font-bold text-xl mb-4 text-purple-900">📊 Truth Table Mastery</h4>
            <p className="mb-4 text-gray-700">This is THE most important table to memorize!</p>
            <div className="overflow-x-auto">
              <table className="w-full text-center text-sm">
                <thead className="bg-purple-100">
                  <tr>
                    <th className="p-2 border">P</th>
                    <th className="p-2 border">Q</th>
                    <th className="p-2 border bg-red-100">~P</th>
                    <th className="p-2 border bg-green-100">P∧Q</th>
                    <th className="p-2 border bg-blue-100">P∨Q</th>
                    <th className="p-2 border bg-yellow-100">P→Q</th>
                    <th className="p-2 border bg-pink-100">P↔Q</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border hover:bg-gray-50">
                    <td className="p-2 border font-bold">F</td>
                    <td className="p-2 border font-bold">F</td>
                    <td className="p-2 border bg-red-50">T</td>
                    <td className="p-2 border bg-green-50">F</td>
                    <td className="p-2 border bg-blue-50">F</td>
                    <td className="p-2 border bg-yellow-50">T</td>
                    <td className="p-2 border bg-pink-50">T</td>
                  </tr>
                  <tr className="border hover:bg-gray-50">
                    <td className="p-2 border font-bold">F</td>
                    <td className="p-2 border font-bold">T</td>
                    <td className="p-2 border bg-red-50">T</td>
                    <td className="p-2 border bg-green-50">F</td>
                    <td className="p-2 border bg-blue-50">T</td>
                    <td className="p-2 border bg-yellow-50">T</td>
                    <td className="p-2 border bg-pink-50">F</td>
                  </tr>
                  <tr className="border hover:bg-gray-50">
                    <td className="p-2 border font-bold">T</td>
                    <td className="p-2 border font-bold">F</td>
                    <td className="p-2 border bg-red-50">F</td>
                    <td className="p-2 border bg-green-50">F</td>
                    <td className="p-2 border bg-blue-50">T</td>
                    <td className="p-2 border bg-yellow-50">F</td>
                    <td className="p-2 border bg-pink-50">F</td>
                  </tr>
                  <tr className="border hover:bg-gray-50">
                    <td className="p-2 border font-bold">T</td>
                    <td className="p-2 border font-bold">T</td>
                    <td className="p-2 border bg-red-50">F</td>
                    <td className="p-2 border bg-green-50">T</td>
                    <td className="p-2 border bg-blue-50">T</td>
                    <td className="p-2 border bg-yellow-50">T</td>
                    <td className="p-2 border bg-pink-50">T</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-orange-50 p-5 rounded-lg border-l-4 border-orange-500">
            <h4 className="font-bold text-lg text-orange-800 mb-3">💡 Key Concepts to Remember</h4>
            <div className="space-y-2 text-sm">
              <p><span className="font-bold">Tautology:</span> Always TRUE (e.g., P ∨ ~P)</p>
              <p><span className="font-bold">Contradiction:</span> Always FALSE (e.g., P ∧ ~P)</p>
              <p><span className="font-bold">Satisfiable:</span> Can be TRUE in some cases</p>
              <p><span className="font-bold">Valid:</span> TRUE in ALL cases</p>
            </div>
          </div>

          <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
            <h4 className="font-bold text-lg text-red-800 mb-3">⚠️ Limitation of Propositional Logic</h4>
            <p className="text-gray-700 mb-2">Cannot express statements about groups or relationships:</p>
            <ul className="list-disc ml-6 text-gray-700 space-y-1">
              <li>"All students are smart" - Can't do this!</li>
              <li>"Some dogs are brown" - Can't do this!</li>
              <li>Need: <span className="font-bold text-red-700">Predicate Logic</span> for this</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 2,
      title: "3. Predicate Logic - More Powerful!",
      icon: BookOpen,
      color: "bg-purple-500",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border-l-4 border-purple-500">
            <h3 className="text-2xl font-bold text-purple-900 mb-4">🚀 Predicate Logic - The Upgrade</h3>
            <p className="text-lg text-gray-700 mb-4">
              Predicate Logic lets us talk about <span className="font-bold">objects</span>, their <span className="font-bold">properties</span>, 
              and <span className="font-bold">relationships</span>. Much more powerful!
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="font-bold text-xl mb-4 text-purple-900">🔍 What's a Predicate?</h4>
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="font-semibold text-purple-900 mb-2">Structure: Predicate(arguments)</p>
                <p className="text-gray-700">Example: <code className="bg-purple-100 px-2 py-1 rounded">IsEven(x)</code></p>
                <p className="text-sm text-gray-600 mt-2">
                  x is the <span className="font-bold">subject</span>, "IsEven" is the <span className="font-bold">predicate</span>
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="font-bold text-green-800 mb-2">✅ Examples:</p>
                  <ul className="space-y-1 text-sm">
                    <li><code>Dog(Fido)</code> - Fido is a dog</li>
                    <li><code>Tall(John)</code> - John is tall</li>
                    <li><code>Loves(Mary, Pizza)</code> - Mary loves pizza</li>
                    <li><code>MotherOf(Sarah, Tom)</code> - Sarah is mother of Tom</li>
                  </ul>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="font-bold text-blue-800 mb-2">🎯 When to use:</p>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li>• Single argument: Properties</li>
                    <li>• Two arguments: Relationships</li>
                    <li>• Multiple: Complex relationships</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="font-bold text-xl mb-4 text-blue-900">🌐 Quantifiers - The Game Changers</h4>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-5 rounded-lg border-2 border-green-300">
                <h5 className="font-bold text-green-800 text-lg mb-3">∀ Universal (For All)</h5>
                <p className="text-gray-700 mb-3">Makes a statement about EVERY object</p>
                
                <div className="space-y-3 text-sm">
                  <div className="bg-white p-3 rounded shadow-sm">
                    <p className="font-semibold">All dogs are happy</p>
                    <p className="text-green-700 font-mono mt-1">∀x (Dog(x) → Happy(x))</p>
                    <p className="text-gray-600 text-xs mt-1">For every x, if x is a dog, then x is happy</p>
                  </div>
                  
                  <div className="bg-white p-3 rounded shadow-sm">
                    <p className="font-semibold">All students are smart</p>
                    <p className="text-green-700 font-mono mt-1">∀x (Student(x) → Smart(x))</p>
                  </div>

                  <div className="bg-white p-3 rounded shadow-sm">
                    <p className="font-semibold">No dog is happy</p>
                    <p className="text-green-700 font-mono mt-1">∀x (Dog(x) → ¬Happy(x))</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-5 rounded-lg border-2 border-blue-300">
                <h5 className="font-bold text-blue-800 text-lg mb-3">∃ Existential (There Exists)</h5>
                <p className="text-gray-700 mb-3">Makes a statement about SOME object(s)</p>
                
                <div className="space-y-3 text-sm">
                  <div className="bg-white p-3 rounded shadow-sm">
                    <p className="font-semibold">Some dogs are happy</p>
                    <p className="text-blue-700 font-mono mt-1">∃x (Dog(x) ∧ Happy(x))</p>
                    <p className="text-gray-600 text-xs mt-1">There exists an x that is a dog AND happy</p>
                  </div>
                  
                  <div className="bg-white p-3 rounded shadow-sm">
                    <p className="font-semibold">Some students are tired</p>
                    <p className="text-blue-700 font-mono mt-1">∃x (Student(x) ∧ Tired(x))</p>
                  </div>

                  <div className="bg-white p-3 rounded shadow-sm">
                    <p className="font-semibold">Someone is happy</p>
                    <p className="text-blue-700 font-mono mt-1">∃x Happy(x)</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-400">
              <h5 className="font-bold text-yellow-800 mb-3">⚠️ CRITICAL RULE - Use correct connector!</h5>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <div className="bg-white p-3 rounded">
                  <p className="font-bold text-green-700">∀ use → (implies)</p>
                  <p className="text-gray-700 mt-1">∀x (Dog(x) → Happy(x))</p>
                  <p className="text-xs text-gray-600 mt-1">If it's a dog, then it's happy</p>
                </div>
                <div className="bg-white p-3 rounded">
                  <p className="font-bold text-blue-700">∃ use ∧ (and)</p>
                  <p className="text-gray-700 mt-1">∃x (Dog(x) ∧ Happy(x))</p>
                  <p className="text-xs text-gray-600 mt-1">Something that IS a dog AND happy</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="font-bold text-xl mb-4 text-orange-900">📝 Practice Examples - Master These!</h4>
            
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800 mb-2">1. Every student is happy</p>
                <p className="text-green-700 font-mono bg-white p-2 rounded">∀x (Student(x) → Happy(x))</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800 mb-2">2. Some students are happy</p>
                <p className="text-blue-700 font-mono bg-white p-2 rounded">∃x (Student(x) ∧ Happy(x))</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800 mb-2">3. No student complained</p>
                <p className="text-green-700 font-mono bg-white p-2 rounded">∀x (Student(x) → ¬Complained(x))</p>
                <p className="text-sm text-gray-600 mt-1">OR: ¬∃x (Student(x) ∧ Complained(x))</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800 mb-2">4. Not every student complained</p>
                <p className="text-blue-700 font-mono bg-white p-2 rounded">¬∀x (Student(x) → Complained(x))</p>
                <p className="text-sm text-gray-600 mt-1">OR: ∃x (Student(x) ∧ ¬Complained(x))</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800 mb-2">5. Every student has visited Canada or Mexico</p>
                <p className="text-green-700 font-mono bg-white p-2 rounded">∀x (Student(x) → (Visited(x,Canada) ∨ Visited(x,Mexico)))</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800 mb-2">6. Nobody likes taxes</p>
                <p className="text-green-700 font-mono bg-white p-2 rounded">∀x ¬Likes(x, Taxes)</p>
                <p className="text-sm text-gray-600 mt-1">OR: ¬∃x Likes(x, Taxes)</p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
            <h4 className="font-bold text-lg text-red-800 mb-3">🎯 Exam Tips for Predicate Logic</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✓ <span className="font-bold">Universal (∀)</span> = use implication (→)</li>
              <li>✓ <span className="font-bold">Existential (∃)</span> = use conjunction (∧)</li>
              <li>✓ Double check: "All" vs "Some" vs "No" vs "Not all"</li>
              <li>✓ Parentheses matter! Use them correctly</li>
              <li>✓ Practice converting English to logic and back</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 3,
      title: "4. Semantic Networks - Visual Knowledge",
      icon: Network,
      color: "bg-orange-500",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-lg border-l-4 border-orange-500">
            <h3 className="text-2xl font-bold text-orange-900 mb-4">🕸️ Semantic Networks</h3>
            <p className="text-lg text-gray-700 mb-4">
              A <span className="font-bold">visual way</span> to represent knowledge using <span className="font-bold">nodes</span> (concepts) 
              and <span className="font-bold">links</span> (relationships). Think of it as a knowledge graph!
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="font-bold text-xl mb-4 text-orange-900">🔧 Components</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-300">
                <h5 className="font-bold text-blue-800 mb-2">Nodes (Circles)</h5>
                <p className="text-sm text-gray-700">Represent objects, concepts, or situations</p>
                <div className="mt-3 flex justify-center">
                  <div className="w-20 h-20 bg-blue-300 rounded-full flex items-center justify-center font-bold text-white">
                    Dog
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border-2 border-green-300">
                <h5 className="font-bold text-green-800 mb-2">Links (Arrows)</h5>
                <p className="text-sm text-gray-700">Represent relationships between nodes</p>
                <div className="mt-3 flex justify-center items-center gap-2">
                  <div className="w-12 h-12 bg-green-300 rounded-full"></div>
                  <div className="text-2xl">→</div>
                  <div className="w-12 h-12 bg-green-300 rounded-full"></div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-300">
                <h5 className="font-bold text-purple-800 mb-2">Labels (Text)</h5>
                <p className="text-sm text-gray-700">Describe the type of relationship</p>
                <div className="mt-3 text-center">
                  <p className="bg-purple-200 inline-block px-3 py-1 rounded font-semibold text-purple-900">is-a</p>
                  <p className="text-xs text-gray-600 mt-2">has, owns, loves, etc.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="font-bold text-xl mb-4 text-blue-900">📊 Common Relationship Types</h4>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="bg-blue-50 p-3 rounded">
                <p className="font-bold text-blue-800">is-a (isa)</p>
                <p className="text-gray-700">Class membership: Dog is-a Animal</p>
              </div>
              <div className="bg-green-50 p-3 rounded">
                <p className="font-bold text-green-800">instance</p>
                <p className="text-gray-700">Specific example: Fido instance Dog</p>
              </div>
              <div className="bg-purple-50 p-3 rounded">
                <p className="font-bold text-purple-800">has-part</p>
                <p className="text-gray-700">Components: Car has-part Engine</p>
              </div>
              <div className="bg-orange-50 p-3 rounded">
                <p className="font-bold text-orange-800">owns / has</p>
                <p className="text-gray-700">Possession: John owns Car</p>
              </div>
              <div className="bg-pink-50 p-3 rounded">
                <p className="font-bold text-pink-800">loves / likes</p>
                <p className="text-gray-700">Preference: Mary loves Pizza</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded">
                <p className="font-bold text-yellow-800">team / works-for</p>
                <p className="text-gray-700">Affiliation: Mike team Cardiff</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="font-bold text-xl mb-4 text-green-900">🎨 Example Network Visualization</h4>
            <div className="bg-gray-50 p-6 rounded-lg">
              <svg viewBox="0 0 600 400" className="w-full">
                <circle cx="300" cy="50" r="40" fill="#3B82F6" stroke="#1E40AF" strokeWidth="3"/>
                <text x="300" y="55" textAnchor="middle" fill="white" fontWeight="bold">Animal</text>
                
                <circle cx="200" cy="150" r="40" fill="#10B981" stroke="#047857" strokeWidth="3"/>
                <text x="200" y="155" textAnchor="middle" fill="white" fontWeight="bold">Mammal</text>
                
                <circle cx="400" cy="150" r="40" fill="#10B981" stroke="#047857" strokeWidth="3"/>
                <text x="400" y="155" textAnchor="middle" fill="white" fontWeight="bold">Bird</text>
                
                <circle cx="100" cy="250" r="40" fill="#8B5CF6" stroke="#6D28D9" strokeWidth="3"/>
                <text x="100" y="255" textAnchor="middle" fill="white" fontWeight="bold">Dog</text>
                
                <circle cx="300" cy="250" r="40" fill="#8B5CF6" stroke="#6D28D9" strokeWidth="3"/>
                <text x="300" y="255" textAnchor="middle" fill="white" fontWeight="bold">Cat</text>
                
                <circle cx="100" cy="350" r="35" fill="#F59E0B" stroke="#D97706" strokeWidth="3"/>
                <text x="100" y="355" textAnchor="middle" fill="white" fontWeight="bold" fontSize="14">Fido</text>
                
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                    <polygon points="0 0, 10 3, 0 6" fill="#1F2937" />
                  </marker>
                </defs>
                
                <line x1="280" y1="85" x2="220" y2="120" stroke="#1F2937" strokeWidth="2" markerEnd="url(#arrowhead)"/>
                <text x="235" y="95" fill="#DC2626" fontWeight="bold" fontSize="12">is-a</text>
                
                <line x1="320" y1="85" x2="380" y2="120" stroke="#1F2937" strokeWidth="2" markerEnd="url(#arrowhead)"/>
                <text x="360" y="95" fill="#DC2626" fontWeight="bold" fontSize="12">is-a</text>
                
                <line x1="175" y1="180" x2="120" y2="220" stroke="#1F2937" strokeWidth="2" markerEnd="url(#arrowhead)"/>
                <text x="130" y="195" fill="#DC2626" fontWeight="bold" fontSize="12">is-a</text>
                
                <line x1="220" y1="180" x2="280" y2="220" stroke="#1F2937" strokeWidth="2" markerEnd="url(#arrowhead)"/>
                <text x="260" y="195" fill="#DC2626" fontWeight="bold" fontSize="12">is-a</text>
                
                <line x1="100" y1="290" x2="100" y2="310" stroke="#1F2937" strokeWidth="2" markerEnd="url(#arrowhead)"/>
                <text x="110" y="305" fill="#059669" fontWeight="bold" fontSize="12">instance</text>
              </svg>
            </div>
            <p className="text-sm text-gray-600 mt-3 text-center italic">
              This shows: Animal → Mammal → Dog → Fido (hierarchy of concepts)
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="font-bold text-xl mb-4 text-purple-900">🔄 Converting to Prolog Facts</h4>
            <p className="text-gray-700 mb-4">Semantic nets can be converted to Prolog facts easily:</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="font-bold text-purple-900 mb-2">Semantic Net:</p>
                <p className="text-sm text-gray-700">Dog → <span className="text-red-600 font-bold">is-a</span> → Mammal</p>
                <p className="text-sm text-gray-700">Fido → <span className="text-green-600 font-bold">instance</span> → Dog</p>
                <p className="text-sm text-gray-700">John → <span className="text-blue-600 font-bold">owns</span> → Fido</p>
              </div>
              
              <div className="bg-gray-100 p-4 rounded-lg font-mono text-sm">
                <p className="text-red-700">isa(dog, mammal).</p>
                <p className="text-green-700">instance(fido, dog).</p>
                <p className="text-blue-700">owns(john, fido).</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-5 rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-bold text-lg text-yellow-800 mb-3">💡 Why Use Semantic Networks?</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✓ <span className="font-bold">Easy to visualize</span> - Humans understand graphs well</li>
              <li>✓ <span className="font-bold">Shows relationships clearly</span> - See connections at a glance</li>
              <li>✓ <span className="font-bold">Inheritance</span> - Properties flow down (Dogs inherit from Mammals)</li>
              <li>✓ <span className="font-bold">Natural representation</span> - Matches how we think about concepts</li>
            </ul>
          </div>

          <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
            <h4 className="font-bold text-lg text-red-800 mb-3">🎯 Exam Tips for Semantic Networks</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✓ Draw nodes as circles/ellipses, clearly labeled</li>
              <li>✓ Use arrows to show direction of relationships</li>
              <li>✓ Always label your arrows (is-a, instance, owns, etc.)</li>
              <li>✓ Remember: is-a = class hierarchy, instance = specific example</li>
              <li>✓ Practice converting Prolog ↔ Semantic nets</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 4,
      title: "5. Practice Quiz - Test Yourself!",
      icon: CheckCircle,
      color: "bg-red-500",
      content: (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-red-50 to-rose-50 p-6 rounded-lg border-l-4 border-red-500">
            <h3 className="text-2xl font-bold text-red-900 mb-4">🎓 Final Exam Practice</h3>
            <p className="text-lg text-gray-700">
              Test your knowledge! These questions are similar to what you'll see on your exam.
            </p>
          </div>

          <QuizSection quizAnswers={quizAnswers} setQuizAnswers={setQuizAnswers} />

          <button
            onClick={() => setShowQuizResults(!showQuizResults)}
            className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-lg transition-colors"
          >
            {showQuizResults ? 'Hide Results' : 'Check Answers'}
          </button>

          {showQuizResults && <QuizResults quizAnswers={quizAnswers} />}
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-2xl p-6 mb-6">
          <h1 className="text-4xl font-bold text-center bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            AI Knowledge Representation
          </h1>
          <p className="text-center text-gray-600 text-lg">Complete Exam Preparation Guide</p>
          <div className="mt-4 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
            <p className="text-yellow-800 font-semibold">🎯 Study Strategy:</p>
            <p className="text-yellow-700 text-sm mt-1">Go through each section carefully → Take notes → Do the practice quiz → Review mistakes</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setCurrentSection(section.id)}
                className={`p-4 rounded-lg shadow-md transition-all transform hover:scale-105 ${
                  currentSection === section.id
                    ? `${section.color} text-white shadow-lg`
                    : 'bg-white text-gray-700 hover:shadow-lg'
                }`}
              >
                <Icon className="mx-auto mb-2" size={24} />
                <p className="text-xs font-semibold text-center">{section.title}</p>
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-6 mb-6">
          {sections[currentSection].content}
        </div>

        <div className="flex justify-between items-center">
          <button
            onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
            disabled={currentSection === 0}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
              currentSection === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600 transform hover:scale-105'
            }`}
          >
            <ChevronLeft size={20} />
            Previous
          </button>

          <div className="text-gray-600 font-semibold">
            Section {currentSection + 1} of {sections.length}
          </div>

          <button
            onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
            disabled={currentSection === sections.length - 1}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
              currentSection === sections.length - 1
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600 transform hover:scale-105'
            }`}
          >
            Next
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="mt-6 text-center text-gray-600 text-sm bg-white rounded-lg p-4 shadow-md">
          <p className="font-semibold text-lg text-gray-800 mb-2">💪 You've Got This!</p>
          <p>Take your time with each section. Understanding is more important than speed.</p>
          <p className="mt-2 text-blue-600 font-semibold">Good luck on your exam tomorrow! 🎓</p>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeRepresentationTutor;